import {
  Suspense,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./dashboard.css";
import {
  getDashboardBadge,
  getDashboardInitialSettings,
  getDashboardInitialThreads,
} from "./api/dashboardBootstrapApi";
import { useAdminSessionSummary } from "../auth/hooks/useAdminSessionSummary";
import { useDashboardGlobalSearch } from "./hooks/useDashboardGlobalSearch";
import {
  DashboardAssetType,
  DashboardRole,
  DashboardSectionId,
  DashboardSettingsState,
  DashboardThread,
} from "./types.js";
import { useDashboardMessages } from "./hooks/useDashboardMessages";
import { DashboardHeader } from "./components/layout/DashboardHeader";
import { DashboardSidebar } from "./components/layout/DashboardSidebar";
import { DashboardMobileBottomNav } from "./components/layout/DashboardMobileBottomNav";
import { CanvasRoutes } from "./components/layout/CanvasRoutes";
import {
  DashboardModalHost,
  DashboardModalState,
} from "./components/modals/DashboardModalHost";
import { ToastProvider } from "./components/primitives/ToastProvider";
import { ProjectsSection } from "./components/sections/ProjectsSection";
import { seedDashboardAssetRuntimeRecord } from "./services/dashboardAssetUploadService";
import { lazyWithRetry } from "../../utils/lazyWithRetry";
import type {
  DashboardGlobalSearchResult,
} from "./hooks/useDashboardGlobalSearch";

const AssetsSection = lazyWithRetry(() =>
  import("./components/sections/AssetsSection").then((module) => ({
    default: module.AssetsSection,
  })),
);
const MessagesSection = lazyWithRetry(() =>
  import("./components/sections/MessagesSection").then((module) => ({
    default: module.MessagesSection,
  })),
);
const SettingsSection = lazyWithRetry(() =>
  import("./components/sections/SettingsSection").then((module) => ({
    default: module.SettingsSection,
  })),
);

function DashboardSectionSuspenseFallback() {
  return (
    <section
      className="flex min-h-[28rem] items-center justify-center rounded-2xl border border-black/10 dark:border-white/10 bg-slate-50/50 dark:bg-slate-950/50"
      data-oid="0zm3uym"
    >
      <div
        className="grid justify-items-center gap-3 text-center"
        data-oid="4uqux2i"
      >
        <div className="text-sm text-slate-400">Loading...</div>
      </div>
    </section>
  );
}

interface AdminDashboardPageProps {
  role: DashboardRole;
  displayName?: string;
  canMutate: boolean;
  onSignOut: () => Promise<void>;
  signOutError: string | null;
  initialAssetTab?: DashboardAssetType;
  initialModal?: DashboardModalState;
  initialSection?: DashboardSectionId;
  initialSettings?: DashboardSettingsState;
  initialThread?: DashboardThread;
}
const EMPTY_MODAL_STATE: DashboardModalState = { kind: "none" };

// Seed the pergola model as a sample 3D asset for the dashboard
seedDashboardAssetRuntimeRecord({
  assetId: 'engine-main-v2',
  previewUrl: '/pergola-camera-export.glb',
  previewKind: 'model',
  companyId: 'weijia',
  productId: 'pergola-pro',
});

function getDashboardSectionFromUrl(): DashboardSectionId | null {
  if (typeof window === "undefined") {
    return null;
  }

  const section = new URLSearchParams(window.location.search).get("section");
  if (section === "projects" || section === "assets" || section === "messages" || section === "settings") {
    return section;
  }

  return null;
}

function getDashboardModalParamsFromUrl(): { kind: "asset" | "project" | null; id: string | null } {
  if (typeof window === "undefined") {
    return { kind: null, id: null };
  }

  const params = new URLSearchParams(window.location.search);
  const kind = params.get("modal");
  const id = params.get("id");

  if ((kind === "asset" || kind === "project") && id) {
    return { kind, id };
  }

  return { kind: null, id: null };
}

function updateDashboardUrlState(params: {
  section: DashboardSectionId;
  modalKind?: "asset" | "project" | null;
  id?: string | null;
  mode: "push" | "replace";
}) {
  if (typeof window === "undefined") {
    return;
  }

  const nextParams = new URLSearchParams(window.location.search);
  nextParams.set("section", params.section);
  if (params.modalKind && params.id) {
    nextParams.set("modal", params.modalKind);
    nextParams.set("id", params.id);
  } else {
    nextParams.delete("modal");
    nextParams.delete("id");
  }

  const nextUrl = `${window.location.pathname}?${nextParams.toString()}#${params.section}`;
  if (params.mode === "push") {
    window.history.pushState({}, "", nextUrl);
  } else {
    window.history.replaceState({}, "", nextUrl);
  }
}

export function AdminDashboardPage({
  role,
  displayName = "Industrial Hub",
  canMutate,
  onSignOut,
  signOutError,
  initialAssetTab = "model",
  initialModal = EMPTY_MODAL_STATE,
  initialSection,
  initialSettings = getDashboardInitialSettings(),
  initialThread,
}: AdminDashboardPageProps) {
  const globalSearch = useDashboardGlobalSearch();
  const { isLoading: isSessionLoading, sessionUser } = useAdminSessionSummary();
  const seededThreads = useMemo(
    () => (initialThread ? getDashboardInitialThreads() : undefined),
    [initialThread],
  );
  const [activeAssetTab, setActiveAssetTab] =
    useState<DashboardAssetType>(initialAssetTab);
  const [settings, setSettings] =
    useState<DashboardSettingsState>(initialSettings);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [modal, setModal] = useState<DashboardModalState>(initialModal);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<DashboardSectionId>(
    initialSection ?? getDashboardSectionFromUrl() ?? "projects",
  );
  const [pendingUrlSelection, setPendingUrlSelection] = useState<{
    kind: "asset" | "project";
    id: string;
  } | null>(() => {
    if (initialSection) {
      return null;
    }

    const selection = getDashboardModalParamsFromUrl();
    return selection.kind && selection.id
      ? { kind: selection.kind, id: selection.id }
      : null;
  });
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const appliedUrlSelectionRef = useRef<string | null>(null);
  const badge = useMemo(() => getDashboardBadge(role), [role]);

  // Sync settings with the real session user as soon as auth resolves.
  // This ensures the header shows the authenticated user's name immediately,
  // rather than waiting for the SettingsSection to mount.
  useEffect(() => {
    if (isSessionLoading || !sessionUser) {
      return;
    }
    const sessionName = sessionUser.name?.trim();
    const sessionEmail = sessionUser.email?.trim();
    if (sessionName || sessionEmail) {
      setSettings((prev) => ({
        ...prev,
        ...(sessionName ? { fullName: sessionName } : {}),
        ...(sessionEmail ? { email: sessionEmail } : {}),
      }));
    }
  }, [isSessionLoading, sessionUser?.name, sessionUser?.email]);

  const {
    activeThread,
    contactSearchQuery,
    errorMessage: messagesError,
    filteredContacts,
    filteredThreads,
    isLoading: messagesLoading,
    openOrCreateThread,
    retryLoad: retryMessagesLoad,
    searchQuery: messageSearchQuery,
    selectThread,
    sendMessage,
    setContactSearchQuery,
    setSearchQuery: setMessageSearchQuery,
    upsertThread,
  } = useDashboardMessages({
    initialSelectedThreadId: initialThread?.id ?? null,
    initialThreads: seededThreads,
  });
  const registerSection = useCallback(
    (_sectionId: DashboardSectionId) => (_node: HTMLElement | null) =>
      undefined,
    [],
  );
  const closeModal = useCallback(() => {
    setModal({ kind: "none" });
    setPendingUrlSelection(null);
    if (typeof window !== "undefined") {
      updateDashboardUrlState({
        section: activeSection,
        modalKind: null,
        id: null,
        mode: "replace",
      });
    }
  }, [activeSection]);
  const selectGlobalSearchResult = useCallback(
    (result: DashboardGlobalSearchResult) => {
      setPendingUrlSelection(null);
      if (result.kind === "project") {
        setActiveSection("projects");
        setModal({
          kind: "project",
          projectId: result.id,
          projectName: result.title,
        });
        updateDashboardUrlState({
          section: "projects",
          modalKind: "project",
          id: result.id,
          mode: "push",
        });
        return;
      }

      setActiveSection("assets");
      setActiveAssetTab(result.asset.type);
      setModal({ kind: "asset", asset: result.asset });
      updateDashboardUrlState({
        section: "assets",
        modalKind: "asset",
        id: result.id,
        mode: "push",
      });
    },
    [],
  );
  const navigateToSection = useCallback(
    (sectionId: DashboardSectionId) => {
      setSidebarOpen(false);
      setPendingUrlSelection(null);
      setActiveSection(sectionId);
      if (typeof window !== "undefined") {
        updateDashboardUrlState({
          section: sectionId,
          modalKind:
            modal.kind === "asset"
              ? "asset"
              : modal.kind === "project"
                ? "project"
                : null,
          id:
            modal.kind === "asset"
              ? modal.asset.id
              : modal.kind === "project"
                ? modal.projectId
                : null,
          mode: "replace",
        });
      }
      scrollContainerRef.current?.scrollTo?.({
        top: 0,
        behavior: "auto",
      });
    },
    [modal],
  );
  useEffect(() => {
    if (initialSection) {
      return;
    }

    function handlePopState() {
      const selection = getDashboardModalParamsFromUrl();
      setPendingUrlSelection(
        selection.kind && selection.id
          ? { kind: selection.kind, id: selection.id }
          : null,
      );
      const section = getDashboardSectionFromUrl();
      if (section) {
        setActiveSection(section);
      }
    }

    window.addEventListener("popstate", handlePopState);
    handlePopState();
    return () => window.removeEventListener("popstate", handlePopState);
  }, [initialSection]);
  useEffect(() => {
    if (!pendingUrlSelection || globalSearch.isLoading) {
      return;
    }

    const selectionKey = `${pendingUrlSelection.kind}:${pendingUrlSelection.id}`;
    if (appliedUrlSelectionRef.current === selectionKey) {
      return;
    }

    if (pendingUrlSelection.kind === "asset") {
      const asset = globalSearch.assets.find(
        (item) => item.id === pendingUrlSelection.id,
      );
      if (asset) {
        appliedUrlSelectionRef.current = selectionKey;
        setActiveSection("assets");
        setActiveAssetTab(asset.type);
        setModal({ kind: "asset", asset });
        setPendingUrlSelection(null);
        return;
      }
    }

    if (pendingUrlSelection.kind === "project") {
      const project = globalSearch.projects.find(
        (item) => item.id === pendingUrlSelection.id,
      );
      if (project) {
        appliedUrlSelectionRef.current = selectionKey;
        setActiveSection("projects");
        setModal({
          kind: "project",
          projectId: project.id,
          projectName: project.name,
        });
        setPendingUrlSelection(null);
        return;
      }
    }

    setPendingUrlSelection(null);
  }, [
    globalSearch.assets,
    globalSearch.isLoading,
    globalSearch.projects,
    pendingUrlSelection,
  ]);
  useEffect(() => {
    if (!initialSection) {
      return;
    }
    navigateToSection(initialSection);
  }, [initialSection, navigateToSection]);

  let activeSectionContent: ReactElement;
  switch (activeSection) {
    case "assets":
      activeSectionContent = (
        <Suspense
          fallback={<DashboardSectionSuspenseFallback data-oid="jz:bgnd" />}
          data-oid="rh12fxb"
        >
          <AssetsSection
            activeTab={activeAssetTab}
            onOpenAsset={(asset) => setModal({ kind: "asset", asset })}
            onTabChange={setActiveAssetTab}
            registerSection={registerSection}
            data-oid="cmeh531"
          />
        </Suspense>
      );

      break;
    case "messages":
      activeSectionContent = (
        <Suspense
          fallback={<DashboardSectionSuspenseFallback data-oid="7d-lev-" />}
          data-oid="wyz0wtj"
        >
          <MessagesSection
            activeThread={activeThread}
            contactSearchQuery={contactSearchQuery}
            contacts={filteredContacts}
            errorMessage={messagesError}
            isLoading={messagesLoading}
            onOpenAttachment={(thread) =>
              setModal({ kind: "attachment", thread })
            }
            onOpenOrCreateThread={openOrCreateThread}
            onRetryLoad={retryMessagesLoad}
            onSearchContactQueryChange={setContactSearchQuery}
            onSearchQueryChange={setMessageSearchQuery}
            onSelectThread={selectThread}
            onSendMessage={sendMessage}
            registerSection={registerSection}
            searchQuery={messageSearchQuery}
            threads={filteredThreads}
            data-oid="w5ep38d"
          />
        </Suspense>
      );

      break;
    case "settings":
      activeSectionContent = (
        <Suspense
          fallback={<DashboardSectionSuspenseFallback data-oid="cql-4j3" />}
          data-oid="hsja6ed"
        >
          <SettingsSection
            canMutate={canMutate}
            onAvatarChange={setAvatarUrl}
            onOpenSecurity={() => setModal({ kind: "security" })}
            onReset={() => setSettings(initialSettings)}
            onSettingsChange={setSettings}
            registerSection={registerSection}
            settings={settings}
            data-oid="vik3wl3"
          />
        </Suspense>
      );

      break;
    case "projects":
    default:
      activeSectionContent = (
        <ProjectsSection
          canMutate={canMutate}
          onCreateThread={upsertThread}
          onOpenProject={(projectId, projectName) =>
            setModal({ kind: "project", projectId, projectName })
          }
          registerSection={registerSection}
          data-oid="x5a48je"
        />
      );

      break;
  }

  return (
    <ToastProvider>
    <div
      className="flex h-screen overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.12),_transparent_34%),linear-gradient(180deg,_#020617_0%,_#0f172a_100%)] text-slate-900 dark:text-slate-100"
      data-oid="rbb7e:n"
    >
      {" "}
      <DashboardSidebar
        activeSection={activeSection}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onNavigate={navigateToSection}
        onSignOut={() => setModal({ kind: "signout" })}
        role={role}
        data-oid="oqf3-ad"
      />{" "}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-oid="vyvml6i">
        {" "}
        <CanvasRoutes />
        <DashboardHeader
          badge={badge}
          globalSearch={globalSearch}
          displayName={settings.fullName || displayName}
          onOpenNavigation={() => setSidebarOpen(true)}
          onOpenSettings={() => navigateToSection("settings")}
          onSelectSearchResult={selectGlobalSearchResult}
          onSignOut={() => setModal({ kind: "signout" })}
          role={role}
          avatarUrl={avatarUrl ?? undefined}
          data-oid="90tulj4"
        />{" "}
        <main
          className="dashboard-content-area min-h-0 min-w-0 flex-1 overflow-y-auto px-4 pb-24 pt-6 sm:px-5 lg:px-6"
          data-testid="dashboard-scroll-container"
          ref={scrollContainerRef}
          data-oid=":-z:39w"
        >
          {" "}
          <div className="flex flex-col gap-10" data-oid=".4_.zqu">
            {" "}
            {signOutError ? (
              <div
                className="mb-6 max-w-md rounded-2xl border border-rose-400/20 bg-rose-950/30 px-4 py-3 text-sm text-rose-800 dark:text-rose-100"
                data-oid="ju7c2ep"
              >
                {" "}
                {signOutError}{" "}
              </div>
            ) : null}{" "}
            {activeSectionContent}{" "}
          </div>{" "}
        </main>{" "}
      </div>{" "}
      <DashboardMobileBottomNav
        activeSection={activeSection}
        onNavigate={navigateToSection}
        data-oid="_jqlhk0"
      />{" "}
      <DashboardModalHost
        modal={modal}
        onClose={closeModal}
        onSignOut={onSignOut}
        onNavigateToMessages={(assetContext) => {
          closeModal();
          navigateToSection("messages");
          if (assetContext) {
            // Select the first thread so the composer is visible,
            // then show a toast with the revision context.
            const firstThread = filteredThreads[0];
            if (firstThread) {
              selectThread(firstThread.id);
              sendMessage(firstThread.id, {
                body: `Requesting revision for asset "${assetContext.assetName}" (ID: ${assetContext.assetId}). Please review and advise on next steps.`,
                attachments: [],
              });
            }
          }
        }}
        data-oid="tovl_z1"
      />{" "}
    </div>
    </ToastProvider>
  );
}
